import type { Applicability, Engine } from '../types';
import { buildUrl } from '../url';
import { priceParam } from './shared';

// Refinement ids for `rh=`. Prime and "sold by Amazon" are stable; star
// ratings and delivery-speed ids vary by department, so they're best-effort.
const REFINEMENTS: Array<[id: string, refinement: string]> = [
  ['prime', 'p_85:2470955011'],
  ['soldByAmazon', 'p_6:ATVPDKIKX0DER'],
  ['fourStars', 'p_72:1248879011'],
  ['tomorrow', 'p_90:8308921011'],
];

// Your order history has its own search, which none of the product filters
// reach.
const notOrders: Applicability = {
  when: { param: 'orders', unset: true },
  whenLabel: 'doesn’t apply to your orders',
};

// Amazon's first orders were in 1995.
const orderYear = (year: unknown) =>
  typeof year === 'number' && year >= 1995 && year <= new Date().getFullYear()
    ? `year-${year}`
    : undefined;

export const amazon: Engine = {
  id: 'amazon',
  name: 'Amazon',
  bang: 'a',
  aliases: ['amazon', 'amz'],
  homepage: 'https://www.amazon.com',
  category: 'Shopping',
  defaultOn: true,
  description: 'Products by price, Prime, seller and rating, or your orders.',
  signIn: 'Your orders need you signed in to Amazon.',
  experimental: true,
  params: [
    {
      id: 'orders',
      type: 'flag',
      token: 'orders',
      label: 'Your orders',
      group: 'Scope',
      help: 'Searches your order history instead of the store.',
    },
    {
      id: 'year',
      when: { param: 'orders', in: ['true'] },
      whenLabel: 'only applies to your orders',
      type: 'number',
      prefix: 'y',
      label: 'Order year',
      group: 'Scope',
      placeholder: '2025',
      help: 'y2025 lists your 2025 orders. Not used when you search words.',
    },
    { ...priceParam, ...notOrders },
    {
      ...notOrders,
      id: 'prime',
      type: 'flag',
      token: 'prime',
      label: 'Prime',
      group: 'Filters',
    },
    {
      ...notOrders,
      id: 'soldByAmazon',
      type: 'flag',
      token: 'amzn',
      label: 'Sold by Amazon',
      group: 'Filters',
      help: 'Excludes third-party marketplace sellers.',
    },
    {
      ...notOrders,
      id: 'fourStars',
      type: 'flag',
      token: '4star',
      label: '4 stars & up',
      group: 'Filters',
      help: 'Best effort: Amazon uses different ids per department.',
    },
    {
      ...notOrders,
      id: 'tomorrow',
      type: 'flag',
      token: 'tmrw',
      label: 'Get it by tomorrow',
      group: 'Filters',
    },
    {
      ...notOrders,
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'price-asc-rank', token: 'low', label: 'Price: low to high' },
        {
          value: 'price-desc-rank',
          token: 'high',
          label: 'Price: high to low',
        },
        { value: 'review-rank', token: 'rated', label: 'Avg. customer review' },
        { value: 'date-desc-rank', token: 'newest', label: 'Newest arrivals' },
        {
          value: 'exact-aware-popularity-rank',
          token: 'pop',
          label: 'Best sellers',
        },
      ],
    },
  ],
  build({ query, values }) {
    if (values.orders === true)
      return query
        ? buildUrl('https://www.amazon.com/your-orders/search', {
            opt: 'ab',
            search: query,
          })
        : buildUrl('https://www.amazon.com/your-orders/orders', {
            timeFilter: orderYear(values.year),
          });
    const rh: string[] = [];
    if (typeof values.price === 'object') {
      const { min, max } = values.price;
      const cents = (n: number | undefined) =>
        n === undefined ? '' : String(n * 100);
      rh.push(`p_36:${cents(min)}-${cents(max)}`);
    }
    for (const [id, refinement] of REFINEMENTS)
      if (values[id] === true) rh.push(refinement);

    return buildUrl('https://www.amazon.com/s', {
      k: query,
      rh: rh.join(','),
      s: values.sort as string | undefined,
    });
  },
};
